// Castintech dashboard — fetch wrapper'lari (notify + paperclip) (TypeScript).
// Faz 9b PR5 — 22 fetch wrapper. Faz 12 PR-D — api.js → api.ts (params typed).
// Faz 15 — apiFetch merkezi error boundary (401 redirect, 5xx + network toast).
//
// Tasarim:
// - Tum fonksiyonlar `Promise<Response>` doner (caller .ok + .json() parse yapar)
// - Body parametreleri tipli (Issue, Approval, vb. payload tipleri)
// - state import etmez (utilities katmani; state UI katmaninin sorumlulugu)
// - apiFetch toast'a tek yonlu bagli (api.ts → ui/toast.ts; geri yon yok, ESM cyclic degil)

import { API, H } from './config.js';
import { toast } from './ui/toast.js';

// ── apiFetch: Merkezi fetch wrapper (Faz 15) ─────────────────────────
// Davranis:
//   • 401 → window.location.replace('/login.html') + throw (caller catch yutar)
//   • 5xx → toast 'Sunucu hatasi: [status]' + throw
//   • Network error (TypeError) → toast 'Baglanti hatasi' + throw
//   • Aksi (2xx/3xx/4xx 401 haric) → Response dondur (caller r.ok + r.json yapar)
//
// Boylece call site'larda `r.status === 401` ve generic 5xx handling
// tekrarlamaz; mevcut `.catch(() => [])` swallow pattern'i (Promise.all
// parallel fetch'ler) korunur — apiFetch throw, caller catch.

export async function apiFetch(url: string, init?: RequestInit): Promise<Response> {
  let res: Response;
  try {
    res = await fetch(url, init);
  } catch (e) {
    // TypeError = network error / CORS / DNS / offline
    toast('Bağlantı hatası', 'error');
    throw e;
  }
  if (res.status === 401) {
    window.location.replace('/login.html');
    throw new Error('apiFetch: 401 unauthorized — redirecting to /login.html');
  }
  // M0 tenant-scope (2026-07-24): Müşteri Paneli girişi aynı `castintech_session`
  // cookie'sini kind='tenant' ile ezer → tüm staff endpoint'leri 403
  // `tenant_scope_forbidden` döner. 401 gibi login'e yönlendirilmezse dashboard
  // sessizce bomboş render olur ("veri yok" sanılır — 2026-07-26 canlı vaka).
  // Yalnız bu hata koduna özel redirect; admin-bearer 403 akışları etkilenmez.
  if (res.status === 403) {
    let code = '';
    try { code = ((await res.clone().json()) as { error?: string })?.error ?? ''; }
    catch { /* JSON değilse normal 403 akışına bırak */ }
    if (code === 'tenant_scope_forbidden') {
      toast('Müşteri oturumu aktif — yönetici paneli için yeniden giriş yapın', 'error');
      window.location.replace('/login.html?reason=tenant-scope');
      throw new Error('apiFetch: 403 tenant_scope_forbidden — redirecting to /login.html');
    }
  }
  if (res.status >= 500 && res.status < 600) {
    toast('Sunucu hatası: ' + res.status, 'error');
    throw new Error('apiFetch: server error ' + res.status);
  }
  return res;
}

// ── Sprint 2 PR-D1 — apiGetJson typed helper ─────────────────────────
//
// /api/v2/* endpoint'leri için. apiFetch'in 401 redirect + 5xx toast davranışını
// kullanır; üstüne JSON parse + `ok:true` guard + query string serialize ekler.
//
// Davranış:
// - 200 + body.ok === true → caller'a typed body döner
// - 200 + body.ok === false → "<resource>_read_failed" gibi backend hatası
//   → caller'a body döndürülür, ama caller kontrol için ok flag'i kullanabilir.
//   Toast yapmıyoruz (5xx toast apiFetch yapıyor; ok:false uygulama tarafı hata).
// - 401 → apiFetch redirect (location.replace login.html)
// - 5xx → apiFetch toast + throw
// - Network → apiFetch toast + throw
//
// Query param serialize: undefined alanlar atlanır; diğerleri encodeURIComponent.
//
// PR-D2'de operational view'lar ve PR-D'nin tüm yeni fetch'leri bu helper'dan
// geçer.

export interface ApiResultOk<T> {
  ok: true;
  data: T;
}
export interface ApiResultFail {
  ok: false;
  error: string;
  message?: string;
}
export type ApiResult<T> = ApiResultOk<T> | ApiResultFail;

export async function apiGetJson<T = unknown>(
  url: string,
  query?: Record<string, string | number | boolean | undefined | null>,
): Promise<ApiResult<T>> {
  let fullUrl = url;
  if (query) {
    const parts: string[] = [];
    for (const [k, v] of Object.entries(query)) {
      if (v === undefined || v === null || v === '') continue;
      parts.push(`${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`);
    }
    if (parts.length) fullUrl = url + (url.includes('?') ? '&' : '?') + parts.join('&');
  }
  const res = await apiFetch(fullUrl);
  // apiFetch 401/5xx üzerinde throw eder; buraya 200/2xx/3xx/4xx (401 hariç) düşer.
  let body: unknown;
  try {
    body = await res.json();
  } catch {
    return { ok: false, error: 'invalid_json', message: 'response was not valid JSON' };
  }
  const obj = body as { ok?: boolean; error?: string; message?: string };
  if (obj && obj.ok === true) {
    return { ok: true, data: body as T };
  }
  return {
    ok: false,
    error: obj?.error ?? 'unknown_error',
    message: obj?.message,
  };
}

// Body payload tipleri (request body shape'leri)
export interface IssuePayload {
  title: string;
  description?: string;
  status?: string;
  priority?: string;
  assigneeAgentId?: string;
  [key: string]: unknown;
}

export interface IssuePatchPayload {
  status?: string | null;
  priority?: string | null;
  assigneeId?: string | null;
  title?: string | null;
  description?: string | null;
  [key: string]: unknown;
}

export interface AccountActionPayload {
  name?: string;
  [key: string]: unknown;
}

// ── Notify endpoints (/castintech-api/*) ─────────────────────────────

export function authMe(): Promise<Response> {
  // NOT: authMe apiFetch kullanmaz; 401'i guardAuth'in kendisi explicit handle eder
  // (apiFetch 401'de redirect yapar, ama bu zaten beklenen guard davranisi olur).
  // Yine de apiFetch ile temizlemek icin kullanilabilir; simdilik raw fetch
  // (guardAuth catch path'te de redirect yaptigindan double-redirect yaratmasin).
  return fetch('/castintech-api/auth/me', { credentials: 'same-origin' });
}

export function getAccounts(): Promise<Response> {
  return apiFetch('/castintech-api/accounts', { credentials: 'same-origin' });
}

export function postAccountAction(path: string, body: AccountActionPayload): Promise<Response> {
  return fetch('/castintech-api/accounts/' + path, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

export function getFallbackStats(): Promise<Response> {
  return apiFetch('/castintech-api/fallback-stats');
}

export function getAgentsFull(): Promise<Response> {
  return fetch('/castintech-api/agents/full', { credentials: 'same-origin' });
}

export function getSkills(): Promise<Response> {
  return fetch('/castintech-api/skills', { credentials: 'same-origin' });
}

export function getTelegramSessions(): Promise<Response> {
  return fetch('/castintech-api/telegram/sessions', { credentials: 'same-origin' });
}

export function postTelegramSend(chatId: string, text: string): Promise<Response> {
  return fetch('/castintech-api/telegram/send', {
    method: 'POST',
    credentials: 'same-origin',
    headers: H as HeadersInit,
    body: JSON.stringify({ chat_id: chatId, text }),
  });
}

export function getSystemHealth(): Promise<Response> {
  return fetch('/castintech-api/system/health', { credentials: 'same-origin' });
}

export function getAuditRecent(qsString: string): Promise<Response> {
  return fetch('/castintech-api/audit/recent?' + qsString, { credentials: 'same-origin' });
}

// ── Paperclip endpoints (${API}/*) ───────────────────────────────────

export function getCompanies(): Promise<Response> {
  return apiFetch(`${API}/companies`, { headers: H as HeadersInit });
}

export function getCompanyAgents(coId: string): Promise<Response> {
  return apiFetch(`${API}/companies/${coId}/agents`, { headers: H as HeadersInit });
}

// Faz 51 — Issues endpoint pagination. Backend tüm 1340 issue'u tek seferde
// gönderiyordu (~5MB JSON, nginx upstream-buffer warns + Cloudflare connection
// pool zorlaması). Default `?limit=500` query payload'u ~%63 azaltır.
// Paperclip /api/companies/:id/issues `?limit=` ve `?status=` query destekler
// (nginx access log'da pattern görüldü). excludeDone=true done/archived hariç
// tutar (Pano "Tamamlandı" kolonu virtualized lazy zaten).
export interface GetIssuesOpts {
  limit?: number;             // default 500 (paperclip cap'i)
  excludeDone?: boolean;      // default false (geri uyumlu)
  status?: string;            // override: explicit comma-separated
}
export function getCompanyIssues(coId: string, opts: GetIssuesOpts = {}): Promise<Response> {
  const params = new URLSearchParams();
  if (opts.status) {
    params.set('status', opts.status);
  } else if (opts.excludeDone) {
    params.set('status', 'backlog,todo,in_progress,blocked,in_review');
  }
  params.set('limit', String(opts.limit ?? 500));
  return apiFetch(`${API}/companies/${coId}/issues?${params.toString()}`, { headers: H as HeadersInit });
}

// Faz 52 — "Tamamlandı" kolonu için ayrı (lazy) fetch. Pano done kolonu user
// tıklayınca/scroll edince yüklenir. Aynı backend endpoint, sadece status=done
// + custom limit. Default behavior değişmez (caller opt-in).
export function getCompanyIssuesDone(coId: string, limit = 200): Promise<Response> {
  const params = new URLSearchParams();
  params.set('status', 'done');
  params.set('limit', String(limit));
  return apiFetch(`${API}/companies/${coId}/issues?${params.toString()}`, { headers: H as HeadersInit });
}

export function getCompanyApprovals(coId: string): Promise<Response> {
  return apiFetch(`${API}/companies/${coId}/approvals`, { headers: H as HeadersInit });
}

// Faz 50 M6 — Central Brain "actionable pending" approvals (P1 executor uyumu).
// /onaylar widget + sidebar badge bunu kullanır; cb-approvals üst banner ile
// aynı kaynak (/api/v2/approvals). actionableOnly=1 → status=pending AND
// executor_status IN (NULL, 'pending').
export function getCbActionableApprovals(): Promise<Response> {
  return apiFetch('/api/v2/approvals?actionableOnly=1&limit=200', {
    credentials: 'same-origin',
  });
}

export function postIssue(coId: string, body: IssuePayload): Promise<Response> {
  return fetch(`${API}/companies/${coId}/issues`, {
    method: 'POST',
    headers: H as HeadersInit,
    body: JSON.stringify(body),
  });
}

export function patchIssue(id: string, body: IssuePatchPayload): Promise<Response> {
  return fetch(`${API}/issues/${id}`, {
    method: 'PATCH',
    headers: H as HeadersInit,
    credentials: 'same-origin',
    body: JSON.stringify(body),
  });
}

export function getIssueComments(id: string): Promise<Response> {
  return fetch(`${API}/issues/${id}/comments`, { headers: H as HeadersInit });
}

export function postIssueComment(id: string, body: string): Promise<Response> {
  return fetch(`${API}/issues/${id}/comments`, {
    method: 'POST',
    headers: H as HeadersInit,
    body: JSON.stringify({ body }),
  });
}

export function getHeartbeatRunLog(runId: string): Promise<Response> {
  return fetch(`${API}/heartbeat-runs/${runId}/log`, { headers: H as HeadersInit });
}

// M6 Dashboard Stabilization (2026-05-15) — P1 Approval Executor entegrasyonu.
// Onaylar widget listesi M6 (PR #333) ile central_brain.approvals'a yonlendi
// (state.approvals merge paperclip + central_brain). approve/reject butonlari
// paperclip legacy /approvals/:id/approve (404 — UUID mismatch) yerine notify
// /api/v2/approvals/:id/{approve|reject} (transitionApproval handler — P1 ADR
// 2026-05-16-approval-executor-map) kullanmali. cb-approvals.ts:153 zaten v2
// endpoint kullaniyor (referans).
export function postApprove(id: string): Promise<Response> {
  return fetch(`/api/v2/approvals/${id}/approve`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    body: '{}',
  });
}

export function postReject(id: string): Promise<Response> {
  return fetch(`/api/v2/approvals/${id}/reject`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    body: '{}',
  });
}

// Sprint 4 — Agent Council insan-override resolve (Yılmaz/staff belirleyici).
// approve/reject anında çözer; backend staff-only guard + outcome.resolvedBy='human'.
export function postCouncilResolve(
  id: string,
  decision: 'approved' | 'rejected',
  reasoning?: string,
): Promise<Response> {
  return fetch(`/api/v2/council/decisions/${id}/resolve`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(reasoning ? { decision, reasoning } : { decision }),
  });
}

// Stream 6 — Council decisions status filter helper (Genel Bakış + Aktif İşler).
// status='approved' default 'open'-only davranışını override eder; Hermes authority
// kararları onaylandıktan sonra da görünür kalır.
export function getCouncilDecisionsByStatus(
  status?: 'open' | 'approved' | 'rejected' | 'expired',
  limit = 50,
): ReturnType<typeof apiGetJson> {
  const q = status ? `?status=${status}&limit=${limit}` : `?limit=${limit}`;
  return apiGetJson(`/api/v2/council/decisions${q}`);
}

// 2026-06-14 düzeltme: getHermesCapability + HermesCapabilityResponse kaldırıldı.
// Hermes paperclip principal'ı değil (agents tablosunda yok); principal_permission_grants
// DB-grant modeli architecturally yanlıştı. Council onayı = governance yetkisi (propose-only).
// Bkz. wiki/decisions/2026-06-14-hermes-operator-authority-grant.md

// 2026-05-30 — Paperclip onayları (REQUEST_BOARD_APPROVAL, hire_agent vb.) central_brain'de
// DEĞİL, Paperclip approvals tablosunda yaşıyor. Bunlar notify Paperclip proxy üzerinden
// POST /api/approvals/:id/{approve|reject} (board key auth) ile çözülür. central_brain v2
// endpoint'i bunlara 404 dönüyordu. state.approvals'taki companyId'li item'lar bu yolu kullanır.
export function postApprovePaperclip(id: string): Promise<Response> {
  return fetch(`/castintech-api/proxy/api/approvals/${id}/approve`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ note: 'Dashboard onayı' }),
  });
}

export function postRejectPaperclip(id: string): Promise<Response> {
  return fetch(`/castintech-api/proxy/api/approvals/${id}/reject`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ note: 'Dashboard reddi' }),
  });
}
